/**
 * WHICH PRODUCT IS THIS BUNDLE BEING SERVED AS.
 *
 * <h2>Why this file did not exist until now</h2>
 *
 * The 2026-10-24 architecture audit looked for a host check and found none: a repo-wide search
 * for {@code location.hostname}, {@code window.location.host} and {@code document.domain} across
 * {@code frontend/src} returned zero matches. One binary serves both products and nothing in it
 * read the URL.
 *
 * <p>What existed instead were three unrelated mechanisms, none of them host-derived:
 *
 * <ul>
 *   <li>a BUILD-TIME {@code REACT_APP_BAR_MODE}, baked into the bundle by the deploy script —
 *       per deployment;</li>
 *   <li>a localStorage {@code toolbar_profile}, set by a dev dropdown and defaulting to ADMIN —
 *       per browser;</li>
 *   <li>the Firebase {@code isAdmin} role, threaded as a prop — per user.</li>
 * </ul>
 *
 * <p>So "the engines are restricted to integralDelta.ai" was, in the running code, an ADMIN-ROLE
 * restriction: an admin on astrostockcharts.com had every one of them. This module supplies the
 * missing axis. It does not yet gate anything — wiring it into a permission check is the next
 * step, and doing both in one change would have made a behaviour change look like a utility.
 *
 * <h2>THE BRAND STRINGS ARE IMPORTED, NEVER RETYPED</h2>
 *
 * {@code NON_ADMIN_BRAND} already exists and is already on screen. Declaring a second copy is how
 * two spellings of one product name come to differ, and the difference is invisible until somebody
 * reads two surfaces side by side.
 *
 * <p>⚠ WITH ONE DELIBERATE EXCEPTION, ADDED AFTER THIS RULE BIT. The integralDelta host brand WAS
 * imported — from {@code SHORT_BAR_WATERMARK}, a toolbar PRESET constant — and renaming that
 * preset's product name silently rebranded the integraldelta.ai host. It is declared locally now,
 * as {@link INTEGRALDELTA_HOST_BRAND}. The rule above is about not retyping ONE value in two
 * places; this was two different values that happened to be equal, which is the opposite case.
 *
 * <p>⚠ Note the casing that follows from that: the brand is {@code 'AstroStockCharts.Com'}, with
 * a capital C, because that is what {@code nonAdminChrome.js} puts on screen today. The audit
 * brief wrote {@code 'AstroStockCharts.com'}; matching the existing surface beat matching the
 * prose, and the one-character difference is recorded here rather than silently resolved.
 *
 * <h2>Deliberately NOT cached</h2>
 *
 * A hostname cannot change inside a single-page session, so a module-level cache would be free.
 * It would also make the resolution untestable — a suite that drives several hostnames through
 * one import would get whichever one it happened to load with. The work is a substring test; it
 * is not worth a correctness hazard.
 */

import { NON_ADMIN_BRAND } from '../config/nonAdminChrome';
import { LONG, SHORT } from './toolbarBarMode';

/** The customer-facing product. */
export const ASC = 'ASC';

/** The research product: the band engines, the astrological studies, the Strategy Designers. */
export const ID = 'ID';

/**
 * The substring each product's domain is recognised by.
 *
 * <p>A SUBSTRING rather than an exact host, because the same product is served from several
 * names — {@code astrostockcharts.com}, {@code www.astrostockcharts.com}, the Firebase Hosting
 * {@code .web.app} and {@code .firebaseapp.com} defaults, and any future subdomain. Matching the
 * registrable name covers all of them without a list somebody has to keep current, and the list
 * going stale would silently resolve a live host to the fallback.
 */
export const ASC_DOMAIN_MARKER = 'astrostockcharts';
export const ID_DOMAIN_MARKER = 'integraldelta';

/**
 * Hostnames that mean "nobody is serving this as a product yet".
 *
 * <p>Exact matches plus the {@code .localhost} / {@code .test} reserved suffixes, rather than a
 * loose {@code includes('local')} — that would capture a real host such as
 * {@code localmarkets.example.com}.
 */
const LOCAL_EXACT = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]', '']);
const LOCAL_SUFFIXES = ['.localhost', '.test', '.local'];

/** True for a development or test host, where the URL carries no product identity. */
export function isLocalHostname(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (LOCAL_EXACT.has(h)) return true;
  return LOCAL_SUFFIXES.some(suffix => h.endsWith(suffix));
}

/**
 * The product a hostname names, or null when it names neither.
 *
 * <p>⚠ ASC IS TESTED FIRST, and that is a decision rather than an ordering accident. The two
 * markers are disjoint in practice, so the only way both match is a contrived host such as
 * {@code integraldelta.astrostockcharts.com}. ASC is the narrower product — it is the one
 * WITHOUT the band engines and the research studies — so resolving an ambiguous host to it fails
 * in the safe direction: a capability is withheld rather than granted by a hostname nobody meant
 * to be meaningful.
 */
function productFromHostname(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (h.includes(ASC_DOMAIN_MARKER)) return ASC;
  if (h.includes(ID_DOMAIN_MARKER)) return ID;
  return null;
}

/**
 * The product a build-time environment override names, or null when none does.
 *
 * <p>Two variables are consulted because two already exist and both already carry the answer:
 *
 * <ul>
 *   <li>{@code REACT_APP_SITE_NAME} holds a brand ({@code 'integralDelta.ai'}), so it is matched
 *       with the SAME domain markers a hostname is — one rule, not two;</li>
 *   <li>{@code REACT_APP_BAR_MODE} holds {@code 'short'} or {@code 'long'}, and SHORT is the
 *       integralDelta toolbar. The values come from {@code toolbarBarMode.js} rather than being
 *       typed here.</li>
 * </ul>
 *
 * <p>SITE_NAME is consulted first: it names the product directly, where the bar mode names a
 * toolbar layout that happens to correlate with it. An unrecognised value in either is ignored
 * rather than guessed at, so a typo falls through to the default instead of selecting a product.
 */
function productFromEnv() {
  const siteName = String(process.env.REACT_APP_SITE_NAME || '').toLowerCase();
  const fromSiteName = productFromHostname(siteName);
  if (fromSiteName) return fromSiteName;

  const barMode = String(process.env.REACT_APP_BAR_MODE || '').toLowerCase();
  if (barMode === SHORT) return ID;
  if (barMode === LONG) return ASC;

  return null;
}

/**
 * ⚠ THE EXPLICIT PER-DEPLOYMENT ANSWER: {@code REACT_APP_PRODUCT=astro|integraldelta}.
 *
 * <h2>Why the other three were not enough</h2>
 *
 * Every mechanism above INFERS the product from something else — a hostname, a brand string, a
 * toolbar layout — and all three fail together for the deployment this was added for: serving
 * integralDelta from a domain that does not say so, such as {@code app.tungaresearch.com} while
 * calling the product integralDelta.ai.
 *
 * <p>Measured, not assumed: {@code REACT_APP_BAR_MODE} is set by NO deploy script, and
 * {@code REACT_APP_SITE_NAME} only by {@code deploy-mumbai.sh}, to {@code Dhanabhyas.com} — which
 * matches neither domain marker. So such a host resolves through every branch to the ASC default,
 * and any rule keyed on ID silently does not apply. That is the worst failure mode available: no
 * error, no warning, and a capability rule that looks implemented and is not.
 *
 * <h2>⚠ CONSULTED BEFORE THE HOSTNAME, which is a precedence change and a safe one</h2>
 *
 * An EXPLICIT setting beating an INFERRED one is the general rule, and it is the only way to
 * serve one product from the other's domain. It changes nothing for any existing deployment
 * because nothing sets this variable yet — the precedence only becomes observable once somebody
 * opts in, which is exactly when they want it to win.
 *
 * <p>Accepts the role vocabulary's own spellings ({@code astro}, {@code integraldelta}) and the
 * two internal codes, so whichever of them a deploy script already has to hand resolves. An
 * unrecognised value returns null rather than guessing, so a typo falls through to the hostname
 * instead of selecting the wrong product.
 */
export const PRODUCT_ENV_VAR = 'REACT_APP_PRODUCT';

function productFromExplicitEnv() {
  const raw = String(process.env.REACT_APP_PRODUCT || '').trim().toLowerCase();
  if (raw === '') return null;
  if (raw === 'astro' || raw === 'asc' || raw === 'astrostockcharts') return ASC;
  if (raw === 'integraldelta' || raw === 'id' || raw === 'integraldelta.ai') return ID;
  return null;
}

/**
 * HOW the product was resolved, for a deployment that needs to check.
 *
 * <p>⚠ FOUR CAUSES LOOK IDENTICAL ON SCREEN. "The integralDelta icons did not hide" is the same
 * picture whether the hostname was unrecognised, {@code REACT_APP_PRODUCT} was unset, it was
 * misspelled, or the deploy never passed it to the build at all — and the symptom is an ABSENCE
 * of hiding, which nobody reports as an error. So the resolution names its own source.
 *
 * @returns `{ product, source }` where source is 'env', 'hostname', 'legacy-env' or 'default'
 */
export function describeHostContext(hostname) {
  const h = typeof hostname === 'string'
    ? hostname
    : (typeof window !== 'undefined' && window.location ? window.location.hostname : '');

  const explicit = productFromExplicitEnv();
  if (explicit) return { product: explicit, source: 'env' };
  const fromHost = productFromHostname(h);
  if (fromHost) return { product: fromHost, source: 'hostname' };
  const legacy = productFromEnv();
  if (legacy) return { product: legacy, source: 'legacy-env' };
  return { product: ASC, source: 'default' };
}

/**
 * WHICH PRODUCT THIS BUNDLE IS BEING SERVED AS.
 *
 * <pre>
 *   REACT_APP_PRODUCT=astro|integraldelta ->  ASC | ID        (explicit, wins)
 *   hostname contains 'astrostockcharts'  ->  ASC
 *   hostname contains 'integraldelta'     ->  ID
 *   otherwise                             ->  REACT_APP_SITE_NAME, then REACT_APP_BAR_MODE
 *   otherwise                             ->  ASC
 * </pre>
 *
 * <p>⚠ THE ENV FALLBACK COVERS ANY UNRECOGNISED HOST, not only localhost — a deliberate widening
 * of the brief, which described it for development only. The reason is a host that genuinely
 * exists: {@code astrocharts-frontend-…run.app} is the retained Cloud Run copy of this
 * frontend, and a preview deploy or a bare IP has the same shape. Under a localhost-only rule
 * every one of them would resolve to the default with no way for the deploy that created them to
 * say otherwise. {@link isLocalHostname} still distinguishes the two cases for any caller that
 * needs to.
 *
 * <p>The default is ASC for the same reason ASC wins an ambiguous hostname: it is the product
 * without the research surface, so an unidentifiable host withholds rather than grants.
 *
 * @param {string} [hostname] the host to resolve; defaults to the browser's own. Passed
 *                            explicitly only by tests and by a caller resolving a URL that is
 *                            not the current page.
 * @returns {'ASC'|'ID'}
 */
export function resolveHostContext(hostname) {
  const h = typeof hostname === 'string'
    ? hostname
    // Guarded because a Jest run in the `node` environment has no window at all. A crash here
    // would take down any module that imports this one, which is the wrong failure for a
    // function whose whole job is to answer a question about the environment.
    : (typeof window !== 'undefined' && window.location ? window.location.hostname : '');

  // EXPLICIT FIRST. See `productFromExplicitEnv`: the other three all infer the product from
  // something else, and all three fail for a deployment that serves one product from a domain
  // naming neither.
  return productFromExplicitEnv() || productFromHostname(h) || productFromEnv() || ASC;
}

/** True when this bundle is being served as the customer-facing AstroStockCharts product. */
export function isAstroStockCharts(hostname) {
  return resolveHostContext(hostname) === ASC;
}

/** True when this bundle is being served as the integralDelta research product. */
export function isIntegralDelta(hostname) {
  return resolveHostContext(hostname) === ID;
}

/**
 * ⚠ THE integralDelta HOST'S OWN BRAND, declared here rather than borrowed.
 *
 * <p>This used to read {@code SHORT_BAR_WATERMARK} — a TOOLBAR PRESET constant — so renaming the
 * short bar's product name silently rebranded the integraldelta.ai HOST. Found when the astro
 * product was renamed to Dhanabhyas and `getHostBrandName('integraldelta.ai')` started answering
 * `Dhanabhyas`: a hostname resolver changed its answer because a layout preference was renamed.
 *
 * <p>The two were equal for as long as they existed, which is exactly why the coupling survived.
 * They answer different questions — what a deployment's domain is called, and what a toolbar
 * preset renames the product to — and this file's own docblock below already said they must not be
 * conflated.
 */
export const INTEGRALDELTA_HOST_BRAND = 'integralDelta.ai';

/**
 * The product name to show, taken from the two constants that already put it on screen.
 *
 * <p>This is the HOST's brand and nothing else. It is not the market watermark (see
 * {@code utils/branding.js}, which maps a market id), and it is not the reader's own override
 * (see {@code utils/appBrand.js}, which is a localStorage rename of the header). Those three
 * answer different questions and a caller that conflates them will show one product's name over
 * another's data.
 *
 * @returns {string} `'AstroStockCharts.Com'` or `'integralDelta.ai'`
 */
export function getHostBrandName(hostname) {
  return resolveHostContext(hostname) === ID ? INTEGRALDELTA_HOST_BRAND : NON_ADMIN_BRAND;
}
